import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ComplexityMeter, complexityTotal } from '@/components/ui/Complexity';
import { cx } from '@/lib/format';
import type { Project } from '@/lib/types';
import { useI18n } from '@/store/preferences';

/**
 * The landing page opens on the evidence rather than on a pitch: every system
 * listed once, ranked by how demanding it was to build.
 */
export function SystemsIndex({ projects, className }: { projects: Project[]; className?: string }) {
  const { t } = useI18n();

  const rows = useMemo(
    () =>
      projects
        .filter((project) => project.scale === 'MAJOR' && project.complexity)
        .sort((a, b) => complexityTotal(b.complexity!) - complexityTotal(a.complexity!)),
    [projects]
  );

  if (rows.length === 0) return null;

  return (
    <section className={cx('plate overflow-hidden', className)} aria-labelledby="systems-index">
      <header className="plate-head">
        <p className="eyebrow" id="systems-index">
          {t.index.title}
        </p>
        <p className="hidden text-xs text-faint sm:block">{t.index.caption}</p>
        <p className="font-mono text-2xs tabular text-faint sm:hidden">{rows.length}</p>
      </header>

      <ol>
        {rows.map((project, index) => (
          <li key={project.id}>
            <Link
              to={`/projects/${project.slug}`}
              className={cx(
                'group relative grid items-center gap-x-5 gap-y-2 px-4 py-4 transition-colors duration-200',
                'sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:px-5',
                'hover:bg-elevated focus-visible:bg-elevated',
                index > 0 && 'border-t border-line'
              )}
            >
              <span
                className="absolute inset-y-0 left-0 w-[2px] scale-y-0 bg-signal transition-transform duration-300 ease-smooth group-hover:scale-y-100"
                aria-hidden
              />

              <span className="min-w-0">
                <span className="h3 block truncate transition-colors duration-200 group-hover:text-accent">
                  {project.title}
                </span>
                <span className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-faint">
                  <span className={cx(project.status === 'IN_PRODUCTION' && 'text-accent')}>
                    {t.status[project.status]}
                  </span>
                  <span aria-hidden>·</span>
                  <span className="truncate">
                    {project.technologies
                      .slice(0, 3)
                      .map((tech) => tech.name)
                      .join(' · ')}
                  </span>
                </span>
              </span>

              <ComplexityMeter
                complexity={project.complexity!}
                animate
                className="justify-self-start sm:justify-self-end"
              />

              <span className="hidden font-mono text-2xs tabular text-faint sm:block sm:w-14 sm:text-right">
                {new Date(project.startDate).getUTCFullYear()}
              </span>
            </Link>
          </li>
        ))}
      </ol>

      <footer className="border-t border-line px-4 py-3 sm:px-5">
        <p className="text-xs leading-relaxed text-faint">{t.complexity.legend}</p>
      </footer>
    </section>
  );
}
